/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../setup";
import {
  failureToast,
  PaginatedValue,
  successToast,
} from "../../../../support/utils";
import { IMember } from "../../models/Member";
import { actions as UserManagementActions } from "../../redux/UserManagementRedux";
import {
  approveMember,
  getMembers,
  rejectMember,
} from "../../redux/UserManagementCRUD";

const PendingApprovalsPage = () => {
  const dispatch = useDispatch();

  const roles = useSelector((state: RootState) => state.userManagement.roles);

  const [loading, setLoading] = useState(false);
  const [paginatedValue, setPaginatedValue] = useState<PaginatedValue<IMember>>(
    new PaginatedValue()
  );
  // Per-member selected role for approval.
  const [selectedRoles, setSelectedRoles] = useState<Record<number, number>>(
    {}
  );

  const handleFetchList = async () => {
    setLoading(true);
    try {
      const result = await getMembers("", "pending");
      setPaginatedValue(result);
      setLoading(false);
    } catch (error) {
      failureToast(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchList();
    dispatch(UserManagementActions.requestRoles());
  }, []);

  // Default each row to the "user" role (fall back to any non-owner role).
  const defaultRoleId = (): number | undefined =>
    (
      roles?.find((role) => role.name?.toLowerCase() === "user") ??
      roles?.find((role) => role.id !== 1)
    )?.id;

  const roleForMember = (memberId: number): number | undefined =>
    selectedRoles[memberId] ?? defaultRoleId();

  const handleApprove = async (member: IMember) => {
    const roleId = roleForMember(member.id);
    if (!roleId) {
      failureToast("Select a role before approving.");
      return;
    }
    setLoading(true);
    try {
      await approveMember(member.id, roleId);
      successToast(`${member.name} has been approved.`);
      await handleFetchList();
    } catch (error) {
      failureToast(error);
      setLoading(false);
    }
  };

  const handleReject = async (member: IMember) => {
    setLoading(true);
    try {
      await rejectMember(member.id);
      successToast(`${member.name} has been rejected.`);
      await handleFetchList();
    } catch (error) {
      failureToast(error);
      setLoading(false);
    }
  };

  return (
    <div
      className={`card card-custom card-flush ${
        loading ? "overlay overlay-block" : ""
      }`}
    >
      <div className="pt-8 card-header">
        <h3 className="card-title">Pending approvals</h3>
      </div>
      <div className={`card-body py-5 ${loading ? "overlay-wrapper" : ""}`}>
        {paginatedValue.data.length === 0 && !loading && (
          <div className="text-muted">No pending registrations.</div>
        )}
        {paginatedValue.data.length > 0 && (
          <table className="table table-row-gray-300 gy-7">
            <thead>
              <tr className="fw-bolder fs-6 text-gray-800">
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedValue.data.map((member, idx) => (
                <tr key={idx} className="align-middle">
                  <td>{member.name}</td>
                  <td>{member.email}</td>
                  <td>
                    <select
                      className="form-select form-select-sm form-select-solid text-capitalize w-150px"
                      value={roleForMember(member.id) ?? ""}
                      onChange={(e) =>
                        setSelectedRoles((prev) => ({
                          ...prev,
                          [member.id]: parseInt(e.target.value, 10),
                        }))
                      }
                    >
                      {roles?.map((role, roleIdx) => {
                        if (role.id === 1) return null;
                        return (
                          <option
                            value={role.id}
                            key={roleIdx}
                            className="text-capitalize"
                          >
                            {role.name}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary me-2"
                      disabled={loading}
                      onClick={() => handleApprove(member)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light-danger"
                      disabled={loading}
                      onClick={() => handleReject(member)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {loading && (
          <div className="overlay-layer rounded bg-dark bg-opacity-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovalsPage;
